import { Suspense } from "react"
import { useMutation } from "@blitzjs/rpc"
import { useSession } from "@blitzjs/auth"
import { BlitzPage } from "@blitzjs/next"
import { useRouter } from "next/router"
import Loading from "app/core/components/Loading"
import Form from "app/core/components/Form"
import { Button } from "app/core/components/Button"
import { FORM_ERROR } from "final-form"
import { HiArrowRight } from "react-icons/hi"
import { BookingLayout } from "app/core/layouts/BookingLayout"
import { useErrorHandler } from "react-error-boundary"
import { StartNewBookingInput } from "app/bookings/validations"
import { z } from "zod"
import { Tracking } from "app/core/tracking"
import { InitialUserDetails } from "pages/book-testing"
import Spinner from "react-md-spinner"
import style from "app/bookings/bookings.module.css"
import startNewBooking from "app/bookings/mutations/startNewBooking"

function MindPrintSignup() {
  const session = useSession({ suspense: false })
  const router = useRouter()
  const [startNewBookingMutation] = useMutation(startNewBooking)
  const handleError = useErrorHandler()

  const schema = session.userId
    ? StartNewBookingInput
    : StartNewBookingInput.extend({
        email: z.string().email(),
      })

  return (
    <div className="bg-gray-100">
      <div className="pt-12 sm:pt-16 lg:pt-20">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl leading-9 font-extrabold text-gray-900 sm:text-4xl sm:leading-10 lg:text-5xl lg:leading-none">
              MindPrint
            </h2>
          </div>
        </div>
      </div>
      <div className="mt-8 sm:mt-12 ">
        <div className="relative">
          <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mx-auto rounded-lg shadow-lg overflow-hidden lg:max-w-none lg:grid grid-cols-1 ">
              <div className="py-2 px-3 text-center bg-gray-50 lg:flex lg:flex-col lg:justify-center lg:p-4 h-full col-span-4">
                <Form
                  schema={schema}
                  initialValues={{
                    bookingType: "mindprint",
                    agreeToTestInUS: true,
                  }}
                  onSubmit={async (values) => {
                    try {
                      if (values.email) {
                        Tracking.setEmail(values.email)
                      }
                      await startNewBookingMutation(values)
                      if (values.email) router.push(`/mind-print/new/1?guest=${values.email}`)
                      else router.push(`/mind-print/new/1`)
                    } catch (error) {
                      if (error.name === "AuthenticationError") {
                        handleError(error)
                      }
                      return {
                        [FORM_ERROR]:
                          "Sorry, we had an unexpected error. Please try again. - " +
                          error.toString(),
                      }
                    }
                  }}
                  className="w-full mx-auto"
                >
                  {(form) => (
                    <div className={style["bookings-container"]}>
                      <Suspense fallback={<Loading />}>
                        <InitialUserDetails />
                      </Suspense>

                      <div className="col-span-3 bg-white px-6 py-8 lg:p-12 text-left">
                        <h3 className="text-2xl leading-8 font-extrabold text-gray-900 sm:text-3xl sm:leading-9">
                          Unlock Your Child’s Potential with Mindprint
                        </h3>
                        <h4 className="text-xl mt-4 font-bold text-gray-600">
                          Get to Know How Your Child Learns
                        </h4>
                        <p className="mt-4 text-base leading-6 text-gray-500">
                          Ever wondered why some subjects click effortlessly for your child while
                          others seem like a struggle? With the{" "}
                          <strong>Mindprint assessment</strong>, you can gain valuable insights into
                          how your child learns—all from the comfort of your home and in just about
                          an hour.
                        </p>
                        <h4 className="text-xl mt-4 font-bold text-gray-600">
                          Why Choose Mindprint?
                        </h4>
                        <h5 className="text-lg mt-4 font-semibold text-gray-600">Research-Based</h5>
                        <p className="mt-4 text-base leading-6 text-gray-500">
                          Rooted in extensive research, the Mindprint assessment is designed to
                          provide you with scientifically-backed insights. This isn&apos;t just
                          another test; it&apos;s a comprehensive tool that delivers a deeper
                          understanding of your child&apos;s unique learning profile.
                        </p>

                        <h5 className="text-lg mt-4 font-semibold text-gray-600">
                          Convenience at Home
                        </h5>
                        <p className="mt-6 text-base leading-6 text-gray-500">
                          No stressful testing centers or rigid schedules. Mindprint allows your
                          child to take the assessment at home, where they feel most comfortable.
                          Imagine turning an ordinary afternoon into a pivotal moment for your
                          child&apos;s educational future.
                        </p>

                        <h4 className="text-xl mt-4 font-bold text-gray-600">Key Benefits</h4>

                        <h5 className="text-lg mt-4 font-semibold text-gray-600">
                          Valuable Insights in About an Hour
                        </h5>
                        <p className="mt-6 text-base leading-6 text-gray-500">
                          An hour-long assessment gives you a detailed report that highlights how
                          your child processes and retains information. Understand their cognitive
                          strengths and areas for improvement, making it easier to tailor their
                          learning experiences for maximum success.
                        </p>

                        <h5 className="text-lg mt-4 font-semibold text-gray-600">
                          Practical Tips Tailored to Your child
                        </h5>
                        <p className="mt-6 text-base leading-6 text-gray-500">
                          Mindprint doesn’t just identify strengths and weaknesses; it provides
                          practical, actionable tips. Help your child leverage their strengths to
                          support their weaknesses, creating a balanced and effective learning
                          strategy. See a sample report.
                        </p>

                        <h4 className="text-xl mt-4 font-bold text-gray-600">
                          Ready to Empower Your Child?
                        </h4>
                        <p className="mt-6 text-base leading-6 text-gray-500">
                          Unlock the secret to your child&apos;s learning potential. With Mindprint,
                          you can confidently support their educational journey, equipped with
                          insights that truly make a difference.
                        </p>

                        <h5 className="text-lg mt-4 font-semibold text-gray-600">
                          Get Started Today
                        </h5>
                        <ul
                          className="mt-1 text-base leading-6 text-gray-500 px-4"
                          style={{ listStyleType: "circle" }}
                        >
                          <li>For ages 8-21</li>
                          <li>Must be taken on a computer with a mouse - not a touchscreen.</li>
                          <li>
                            Please allow 5 business days for your account to be set up after
                            registration
                          </li>
                        </ul>
                      </div>

                      <Button disabled={form.invalid} className="px-5">
                        {form.submitting ? (
                          <Spinner size={26} singleColor="#ffffff" />
                        ) : (
                          <>
                            Next <HiArrowRight className="mt-px ml-3" />
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </Form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const MindPrintPage: BlitzPage = () => {
  return (
    <Suspense fallback={<div />}>
      <MindPrintSignup />
    </Suspense>
  )
}

MindPrintPage.getLayout = (page) => (
  <BookingLayout title="Mind Print Testing Registration" subtitle="Mind Print">
    {page}
  </BookingLayout>
)

export default MindPrintPage
